import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { Movie, Paginated } from "@/types";

export function useMovies(
  filters: {
    status?: string;
    search?: string;
    hidePast?: boolean;
    cursor?: string;
    limit?: number;
  } = {},
) {
  return useQuery({
    queryKey: ["movies", filters],
    queryFn: () => {
      const params: Record<string, string> = {
        limit: String(filters.limit ?? 25),
      };
      if (filters.status && filters.status !== "all") params.status = filters.status;
      if (filters.search) params.search = filters.search;
      if (filters.hidePast) params.hidePast = "true";
      if (filters.cursor) params.cursor = filters.cursor;
      return api.get<Paginated<Movie>>("/admin/movies", params);
    },
  });
}

export function useDeleteMovie() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/admin/movies/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["movies"] }),
  });
}

export function useUpdateMovieStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      api.put<Movie>(`/admin/movies/${id}`, { status }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["movies"] }),
  });
}

export interface UpdateMovieBody {
  title?: string;
  originalTitle?: string;
  description?: string;
  status?: string;
  filmwebRating?: string | null;
  imdbRating?: string | null;
  rtScore?: string | null;
  filmwebUrl?: string | null;
  year?: number | null;
  runtime?: number | null;
  ticketUrl?: string | null;
  countryCode?: string | null;
  room?: string | null;
}

export function useUpdateMovie() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateMovieBody }) =>
      api.put<Movie>(`/admin/movies/${id}`, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["movies"] }),
  });
}

export interface CreateMovieBody {
  title: string;
  date: string;
  originalTitle?: string;
  description?: string;
  status?: string;
  filmwebRating?: string | null;
  imdbRating?: string | null;
  rtScore?: string | null;
  filmwebUrl?: string | null;
  year?: number | null;
  runtime?: number | null;
  countryCode?: string | null;
  language?: string | null;
  ticketUrl?: string | null;
  room?: string | null;
}

export function useCreateMovie() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateMovieBody) => api.post<Movie>("/admin/movies", body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["movies"] }),
  });
}

export interface EnrichMoviesResult {
  results: { id: string; ok: boolean }[];
}

export function useEnrichMovies() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => api.post<EnrichMoviesResult>("/admin/movies/enrich", { ids }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["movies"] }),
  });
}
